import React, {Ref, useContext, useMemo} from 'react';
import {observer} from 'mobx-react';
import Heading from 'js/components/Common/Heading/Heading';
import Input from 'js/components/Common/FormComponents/Input/Input';
import {CommonContext} from 'js/context/CommonContext/CommonContext';
import DataValidator from 'js/components/Common/DataValidator/DataValidator';
import ExpandableSection from 'js/components/Common/ExpandableSection/ExpandableSection';

import {TabInfo} from '../LearningPath';
import classes from './FilterContent.module.pcss';
import LearningPathStore from 'js/store/LearningPathStore';
import CompletedMessage from './CompletedMessage/CompletedMessage';
import BeforeCompletedMessage from './CompletedMessage/BeforeCompletedMessage';

interface Props {
    nodeRef: Ref<any>,
    postId: number,
    firstTabInfo: TabInfo,
    secondTabInfo: TabInfo
}

const FilterContent = ({nodeRef, postId, firstTabInfo, secondTabInfo}: Props) => {
    const content = LearningPathStore.activeFAQContent;
    const {apiPath} = useContext(CommonContext);
    const checkedAll = LearningPathStore.getAllCheckedFAQStatus();
    const beforeCompleteMessage = LearningPathStore.activeMainTab === 'first_item' ?
        firstTabInfo?.confirmCompletion : secondTabInfo?.confirmCompletion;

    const subtitle = useMemo(() => {
        if (checkedAll) {
            return 'This learning path is now complete.';
        }
        return 'Did you complete this learning path?';
    }, [checkedAll]);

    return (
        <DataValidator data={content?.length > 0}>
            <div className={`${classes.FilterContent} ${checkedAll ? classes.Completed : ''}`}>
                {content?.map((item, index) => {
                        const {ufid, caseHeader, caseBody} = item || {};
                        const lastClickedElementId = LearningPathStore.lastClickedElement;
                        const lastClickedElCondition = lastClickedElementId === ufid && lastClickedElementId !== 'check_all';
                        return (
                            <ExpandableSection
                                isOpened={index === 0}
                                postId={postId}
                                key={ufid}
                                id={ufid}
                                title={caseHeader}
                                info={caseBody}
                                withStatus={true}
                                isStatusChecked={LearningPathStore.getFAQItemStatus(ufid)}
                                statusCallback={
                                    e =>
                                        LearningPathStore.completeItemOnChange
                                        (e, apiPath, postId, lastClickedElCondition)
                                }
                                showBeforeCompleteMessage={lastClickedElCondition}
                                beforeCompletedMessage={beforeCompleteMessage}
                                nodeRef={nodeRef}/>
                        );
                    }
                )
                }
                <Heading type={'h5'} text={subtitle} />
                <Input
                    withLabel={true}
                    type={'checkbox'}
                    id={'check_all'}
                    slug={'check_all'}
                    additionalClass={`${classes.CheckAll} ${checkedAll ? classes.Checked : ''}`}
                    name={'check_all'}
                    checked={checkedAll}
                    onChange={() => {
                        if (checkedAll) {
                            return;
                        }
                        LearningPathStore.lastClickedElement = 'check_all';
                    }}
                    labelText={checkedAll ? 'Completed' : 'Yes, I Did!'}
                    value={'check_all'} />
                <BeforeCompletedMessage
                    left={'0'}
                    message={beforeCompleteMessage}
                    show={LearningPathStore.lastClickedElement === 'check_all'}
                    postId={postId}
                    group={true} />
                <CompletedMessage
                    pageId={postId}
                        firstTabInfo={firstTabInfo}
                    secondTabInfo={secondTabInfo} />
            </div>
        </DataValidator>
    );
};

export default observer(FilterContent);
